import { Activity, TSession } from '@valuecase/common'
import { Duration } from 'luxon'
import { useMemo, useRef } from 'react'
import { IonIcon } from '../../Icons/IonIcon'
import { cn } from '../../lib/utils'
import { useBreakpoint } from '../../utils'

function ActivitySummaryCard({
  activities,
  sessions,
  sessionCount,
}: {
  activities: Activity[]
  sessions: TSession[]
  sessionCount: number
}) {
  const buyerActivities = useMemo(() => {
    return activities.filter((activity) => activity.creatorType === 'BUYER')
  }, [activities])
  const containerRef = useRef<HTMLDivElement>(null)
  const isNarrow = useBreakpoint(260, containerRef)

  const stakeholders = useMemo(() => {
    const buyers = new Set<string>()
    buyerActivities.forEach((activity) => {
      buyers.add(activity.email || activity.name || 'Anonymous Stakeholder')
    })
    return buyers.size
  }, [buyerActivities])

  const totalTimeSpent = useMemo(() => {
    const summedDuration = sessions.reduce(
      (acc, session) => acc.plus(Duration.fromObject(session.duration)),
      Duration.fromObject({ minutes: 0 }),
    )

    return Math.round(summedDuration.normalize().shiftTo('minutes').get('minutes'))
  }, [sessions])

  return (
    <div
      ref={containerRef}
      className={cn('flex w-full mt-2 p-3 border-solid border border-grey-s2 rounded-lg bg-white', {
        'flex-col gap-3': isNarrow,
        'flex-row justify-between': !isNarrow,
      })}
    >
      <div
        className={cn('flex', {
          'justify-between items-center': isNarrow,
          'flex-col w-1/4': !isNarrow,
        })}
      >
        <div className={'flex items-center gap-1'}>
          <IonIcon name={'person-outline'} className={'text-grey-s3 w-4 h-4'}></IonIcon>
          <p className={'text-grey-s6 text-lg font-bold'}>{stakeholders}</p>
        </div>
        <h3
          className={cn('text-grey-s5 text-xxs leading-[14px]', {
            'ml-2': isNarrow,
          })}
        >
          Stakeholders
        </h3>
      </div>

      <div
        className={cn('', {
          'flex gap-2 w-1/4': !isNarrow,
        })}
      >
        {!isNarrow && <div className={'w-[1px] bg-grey-s2'}></div>}

        <div
          className={cn('flex', {
            'justify-between items-center': isNarrow,
            'flex-col': !isNarrow,
          })}
        >
          <div className={'flex items-center gap-1'}>
            <IonIcon name={'log-in-outline'} className={'text-grey-s3 w-4 h-4'}></IonIcon>
            <p className={'text-grey-s6 text-lg font-bold'}>{sessionCount}</p>
          </div>
          <h3
            className={cn('text-grey-s5 text-xxs leading-[14px]', {
              'ml-2': isNarrow,
            })}
          >
            Visits
          </h3>
        </div>
      </div>

      <div
        className={cn('', {
          'flex gap-2 w-1/4': !isNarrow,
        })}
      >
        {!isNarrow && <div className={'w-[1px] bg-grey-s2'}></div>}

        <div
          className={cn('flex', {
            'justify-between items-center': isNarrow,
            'flex-col': !isNarrow,
          })}
        >
          <div className={'flex items-center gap-1'}>
            <IonIcon name={'c_activity'} className={'text-grey-s3 w-4 h-4'}></IonIcon>
            <p className={'text-grey-s6 text-lg font-bold'}>{buyerActivities.length}</p>
          </div>
          <h3
            className={cn('text-grey-s5 text-xxs leading-[14px]', {
              'ml-2': isNarrow,
            })}
          >
            Activities
          </h3>
        </div>
      </div>

      <div
        className={cn('', {
          'flex gap-2 w-1/4': !isNarrow,
        })}
      >
        {!isNarrow && <div className={'w-[1px] bg-grey-s2'}></div>}

        <div
          className={cn('flex min-w-0', {
            'justify-between items-center': isNarrow,
            'flex-col': !isNarrow,
          })}
        >
          <div className={'flex items-center gap-1'}>
            <IonIcon name={'hourglass-outline'} className={'text-grey-s3 w-4 h-4'}></IonIcon>
            <p
              className={
                'text-grey-s6 text-lg font-bold whitespace-nowrap overflow-hidden text-ellipsis'
              }
            >
              {totalTimeSpent} Min.
            </p>
          </div>
          <h3
            className={cn('text-grey-s5 text-xxs leading-[14px]', {
              'ml-2': isNarrow,
            })}
          >
            Total time spent
          </h3>
        </div>
      </div>
    </div>
  )
}

export default ActivitySummaryCard
